// Onglet « Schéma fonctionnel » : relations du système (équipements, bus, passerelles, alimentations, réseaux IP).
// Dessin manipulable + vue tableau équivalente, annuler / rétablir, enregistrement automatique, résolution de conflit.
import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Maximize2, Plus, Redo2, Settings2, Undo2, ZoomIn, ZoomOut, Network } from 'lucide-react';
import { api } from '../../lib/api.ts';
import { useSession } from '../../lib/session.tsx';
import {
  Alerte, Bouton, Chargement, Dialogue, EtatErreur, EtatVide, IndicateurEnregistrement, useConfirmation, useNotifier,
} from '../../components/ui.tsx';
import { CanevasSchema, H_NOEUD, L_NOEUD, type CanevasPoignee, type Selection } from './CanevasSchema.tsx';
import { ProprietesSchema, typeCommun } from './ProprietesSchema.tsx';
import { TableauSchema } from './TableauSchema.tsx';
import { useEditeurSchema } from './useEditeurSchema.ts';
import {
  LIBELLE_LIAISON, LIBELLE_NOEUD, TYPES_LIAISON, TYPES_NOEUD, nouvelId, type Donnees, type Liaison, type Noeud, type SchemaVue, type TypeLiaison, type TypeNoeud,
} from './modele.ts';

export function useEtroit(requete = '(max-width: 900px)') {
  const [v, setV] = useState(() => typeof window !== 'undefined' && window.matchMedia(requete).matches);
  useEffect(() => {
    const m = window.matchMedia(requete);
    const f = () => setV(m.matches);
    m.addEventListener('change', f);
    return () => m.removeEventListener('change', f);
  }, [requete]);
  return v;
}

export function OngletSchema({ dossierId }: { dossierId: string }) {
  const q = useQuery({ queryKey: ['conception-schemas', dossierId], queryFn: () => api.get<SchemaVue[]>(`/api/dossiers/${dossierId}/schemas`) });
  if (q.isLoading) return <Chargement lignes={5} libelle="Ouverture du schéma…" />;
  if (q.isError) return <EtatErreur erreur={q.error} reessayer={() => q.refetch()} />;
  const s = q.data?.[0];
  if (!s) return <EtatVide icone={<Network />} titre="Aucun schéma pour ce dossier" texte="Vos droits ne permettent pas d’en créer un." />;
  return <Editeur key={s.id} schema={s} dossierId={dossierId} />;
}

function Legende({ donnees }: { donnees: Donnees }) {
  const types = TYPES_LIAISON.filter((t) => donnees.links.some((l) => l.type === t.id));
  return (
    <div className="cch-legende" aria-label="Légende du schéma">
      <span className="cch-legende-item"><svg width="40" height="10" aria-hidden><line x1="2" y1="5" x2="38" y2="5" className="cch-legende-trait" /></svg>Trait plein : compatibilité étayée par une source</span>
      <span className="cch-legende-item"><svg width="40" height="10" aria-hidden><line x1="2" y1="5" x2="38" y2="5" className="cch-legende-trait cch-legende-pointille" /></svg>Pointillés : à vérifier</span>
      {types.map((t) => (
        <span key={t.id} className="cch-legende-item"><span className={`cch-pastille cch-l-${t.id}`} aria-hidden />{t.libelle}{t.detail ? ` (${t.detail})` : ''}</span>
      ))}
    </div>
  );
}

function Editeur({ schema, dossierId }: { schema: SchemaVue; dossierId: string }) {
  const { peut } = useSession();
  const ecriture = peut('conception.ecrire');
  const ed = useEditeurSchema(schema, ecriture);
  const qc = useQueryClient();
  const etroit = useEtroit();
  const [mode, setMode] = useState<'dessin' | 'tableau'>(() => (window.matchMedia('(max-width: 900px)').matches ? 'tableau' : 'dessin'));
  const [selection, setSelection] = useState<Selection>(null);
  const [dialogue, setDialogue] = useState(false);
  const [focus, setFocus] = useState({ cle: '', jeton: 0 });   // demande explicite de placer le focus dans le panneau
  const [typeAjout, setTypeAjout] = useState<TypeNoeud>('automate');
  const [zoom, setZoom] = useState(1);
  const [conflitMasque, setConflitMasque] = useState(false);
  const canevas = useRef<CanevasPoignee>(null);
  const panneau = useRef<HTMLElement>(null);
  const { confirmer, element: confirmation } = useConfirmation();
  const notifier = useNotifier();
  const d = ed.donnees;

  // La sélection disparaît si l'objet n'existe plus (annulation, conflit).
  useEffect(() => {
    if (selection && !(selection.type === 'noeud' ? d.nodes.some((n) => n.id === selection.id) : d.links.some((l) => l.id === selection.id))) setSelection(null);
  }, [d, selection]);
  useEffect(() => { if (ed.conflit) setConflitMasque(false); }, [ed.conflit]);
  // Les autres vues du dossier (quantités) relisent le schéma après enregistrement.
  useEffect(() => { if (ed.etat === 'enregistre') void qc.invalidateQueries({ queryKey: ['conception-quantites', dossierId] }); }, [ed.etat, qc, dossierId]);

  const ouvrirProprietes = useCallback((s?: NonNullable<Selection>) => {
    if (s) setSelection(s);
    const cible = s ?? selection;
    if (etroit) setDialogue(true);
    else if (cible) {
      setFocus((f) => ({ cle: `${cible.type}-${cible.id}`, jeton: f.jeton + 1 }));
      window.setTimeout(() => panneau.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' }), 0);
    }
  }, [etroit, selection]);

  // Raccourcis annuler / rétablir (hors champs de saisie, qui gardent leur propre annulation).
  useEffect(() => {
    if (!ecriture) return;
    const touche = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && (['INPUT', 'TEXTAREA', 'SELECT'].includes(t.tagName) || t.isContentEditable)) return;
      if (!(e.ctrlKey || e.metaKey)) return;
      const k = e.key.toLowerCase();
      if (k === 'z' && !e.shiftKey) { e.preventDefault(); ed.annuler(); }
      else if (k === 'y' || (k === 'z' && e.shiftKey)) { e.preventDefault(); ed.retablir(); }
    };
    window.addEventListener('keydown', touche);
    return () => window.removeEventListener('keydown', touche);
  }, [ecriture, ed.annuler, ed.retablir]);

  const ajouter = () => {
    if (d.nodes.length >= 500) { notifier('Un schéma est limité à 500 équipements.', true); return; }
    const i = d.nodes.length;
    let pos = { x: (i % 4) * (L_NOEUD + 40), y: Math.floor(i / 4) * (H_NOEUD + 48) };
    if (mode === 'dessin' && canevas.current) {
      const c = canevas.current.centre();
      const occupe = (x: number, y: number) => d.nodes.some((n) => Math.abs(n.x - x) < L_NOEUD && Math.abs(n.y - y) < H_NOEUD);
      pos = c; let essai = 0;
      while (occupe(pos.x, pos.y) && essai < 40) { essai++; pos = { x: c.x + (essai % 5) * 32, y: c.y + Math.ceil(essai / 5) * (H_NOEUD + 16) }; }
    }
    const n: Noeud = { id: nouvelId('n'), type: typeAjout, label: `${LIBELLE_NOEUD[typeAjout]} ${d.nodes.filter((x) => x.type === typeAjout).length + 1}`,
      fabricant: null, reference: null, zone: null, protocoles: [], x: pos.x, y: pos.y, note: null };
    ed.modifier((x) => ({ ...x, nodes: [...x.nodes, n] }));
    ouvrirProprietes({ type: 'noeud', id: n.id });
  };

  const deplacer = useCallback((id: string, x: number, y: number, cle: string) =>
    ed.modifier((dd) => ({ ...dd, nodes: dd.nodes.map((n) => (n.id === id ? (n.x === x && n.y === y ? n : { ...n, x, y }) : n)) }), cle), [ed.modifier]);
  const majNoeud = useCallback((id: string, champ: Partial<Noeud>, cle: string) =>
    ed.modifier((dd) => ({ ...dd, nodes: dd.nodes.map((n) => (n.id === id ? { ...n, ...champ } : n)) }), cle), [ed.modifier]);
  const majLiaison = useCallback((id: string, champ: Partial<Liaison>, cle: string) =>
    ed.modifier((dd) => ({ ...dd, links: dd.links.map((l) => (l.id === id ? { ...l, ...champ } : l)) }), cle), [ed.modifier]);

  const relier = useCallback((de: string, vers: string, type?: TypeLiaison) => {
    if (de === vers) return;
    if (d.links.length >= 1500) { notifier('Un schéma est limité à 1 500 liaisons.', true); return; }
    const t = type ?? typeCommun(d.nodes.find((n) => n.id === de), d.nodes.find((n) => n.id === vers));
    const l: Liaison = { id: nouvelId('l'), de, vers, type: t, libelle: null, compatibilite: 'a_verifier', source: null };
    ed.modifier((dd) => ({ ...dd, links: [...dd.links, l] }));
    setSelection({ type: 'liaison', id: l.id });
    notifier(`Liaison ${LIBELLE_LIAISON[t]} créée, compatibilité « à vérifier ».`);
  }, [d, ed.modifier, notifier]);

  const supprimer = useCallback(async (s: NonNullable<Selection>) => {
    if (s.type === 'noeud') {
      const n = d.nodes.find((x) => x.id === s.id); if (!n) return;
      const nb = d.links.filter((l) => l.de === n.id || l.vers === n.id).length;
      const ok = await confirmer('Supprimer cet équipement ?',
        `« ${n.label} »${nb ? ` et ses ${nb} liaison(s)` : ''} seront retirés du schéma. Vous pourrez annuler avec « Annuler » (Ctrl+Z).`, 'Supprimer l’équipement');
      if (!ok) return;
      ed.modifier((dd) => ({ nodes: dd.nodes.filter((x) => x.id !== n.id), links: dd.links.filter((l) => l.de !== n.id && l.vers !== n.id) }));
    } else {
      const l = d.links.find((x) => x.id === s.id); if (!l) return;
      const ok = await confirmer('Supprimer cette liaison ?', `La liaison ${LIBELLE_LIAISON[l.type]} sera retirée du schéma. Vous pourrez annuler (Ctrl+Z).`, 'Supprimer la liaison');
      if (!ok) return;
      ed.modifier((dd) => ({ ...dd, links: dd.links.filter((x) => x.id !== l.id) }));
    }
    setSelection(null); setDialogue(false);
  }, [d, confirmer, ed.modifier]);

  const props = selection && (
    <ProprietesSchema dossierId={dossierId} donnees={d} selection={selection} sources={ed.sources} lectureSeule={!ecriture}
      majNoeud={majNoeud} majLiaison={majLiaison} relier={relier} supprimer={supprimer} choisir={setSelection}
      focusJeton={!etroit && focus.cle === `${selection.type}-${selection.id}` ? focus.jeton : 0} key={`${selection.type}-${selection.id}`} />
  );
  const titreProps = selection?.type === 'noeud' ? 'Équipement' : 'Liaison';

  return (
    <div className="cch-editeur pile">
      <div className="cch-barre" role="toolbar" aria-label="Outils du schéma">
        <div className="segment" role="group" aria-label="Affichage du schéma">
          <button type="button" aria-pressed={mode === 'dessin'} onClick={() => setMode('dessin')}>Dessin</button>
          <button type="button" aria-pressed={mode === 'tableau'} onClick={() => setMode('tableau')}>Tableau</button>
        </div>
        {ecriture && (
          <div className="cch-ajout">
            <select className="saisie" value={typeAjout} onChange={(e) => setTypeAjout(e.target.value as TypeNoeud)} aria-label="Type d’équipement à ajouter">
              {TYPES_NOEUD.map((t) => <option key={t.id} value={t.id}>{t.libelle}</option>)}
            </select>
            <Bouton variante="principal" icone={<Plus aria-hidden />} onClick={ajouter}>Ajouter</Bouton>
          </div>
        )}
        {ecriture && (
          <div className="groupe-btn" role="group" aria-label="Historique">
            <Bouton className="btn-icone" icone={<Undo2 aria-hidden />} onClick={ed.annuler} disabled={!ed.peutAnnuler} aria-label="Annuler (Ctrl+Z)" title="Annuler (Ctrl+Z)" />
            <Bouton className="btn-icone" icone={<Redo2 aria-hidden />} onClick={ed.retablir} disabled={!ed.peutRetablir} aria-label="Rétablir (Ctrl+Y)" title="Rétablir (Ctrl+Y)" />
          </div>
        )}
        {mode === 'dessin' && (
          <div className="groupe-btn cch-zoom" role="group" aria-label="Zoom">
            <Bouton className="btn-icone" icone={<ZoomOut aria-hidden />} onClick={() => canevas.current?.zoomer(1 / 1.2)} aria-label="Zoom arrière" />
            <span className="tres-petit chiffre cch-zoom-valeur" aria-live="polite">{Math.round(zoom * 100)} %</span>
            <Bouton className="btn-icone" icone={<ZoomIn aria-hidden />} onClick={() => canevas.current?.zoomer(1.2)} aria-label="Zoom avant" />
            <Bouton icone={<Maximize2 aria-hidden />} onClick={() => canevas.current?.ajuster()}>Ajuster</Bouton>
          </div>
        )}
        {etroit && selection && mode === 'dessin' && <Bouton icone={<Settings2 aria-hidden />} onClick={() => ouvrirProprietes()}>Propriétés</Bouton>}
        <span className="cch-barre-fin">{ecriture ? <IndicateurEnregistrement etat={ed.etat} quand={ed.quand} /> : <span className="tres-petit discret">Lecture seule</span>}</span>
      </div>

      {ed.erreur && <Alerte ton={ed.etat === 'local' ? 'info' : 'danger'} titre="Enregistrement impossible">{ed.erreur}</Alerte>}
      {!!ed.corrections?.length && (
        <Alerte ton="ambre" titre={`${ed.corrections.length} compatibilité(s) ramenée(s) à « à vérifier » par le contrôle`}>
          {ed.corrections.slice(0, 3).map((c) => c.message).join(' ')}{ed.corrections.length > 3 ? ' …' : ''}
        </Alerte>
      )}
      {ed.conflit && conflitMasque && (
        <Alerte ton="ambre" titre="Conflit non résolu : l’enregistrement automatique est suspendu."
          action={<Bouton compact onClick={() => setConflitMasque(false)}>Résoudre</Bouton>} />
      )}

      <div className={`cch-espace ${etroit ? '' : 'cch-espace-panneau'}`}>
        <div className="cch-zone pile">
          {mode === 'dessin' ? (
            <>
              <div className="cch-canevas-cadre">
                <CanevasSchema ref={canevas} donnees={d} selection={selection} choisir={setSelection} deplacer={deplacer}
                  relier={(a, b) => relier(a, b)} editer={() => ouvrirProprietes()} supprimer={supprimer} lectureSeule={!ecriture} surZoom={setZoom} />
                {!d.nodes.length && (
                  <div className="cch-canevas-vide">
                    <EtatVide centre icone={<Network />} titre="Schéma vide"
                      texte={ecriture ? 'Choisissez un type d’équipement puis « Ajouter ». Reliez ensuite les équipements depuis la poignée ou avec « Relier à… ».' : 'Aucun équipement n’a encore été ajouté.'} />
                  </div>
                )}
              </div>
              <Legende donnees={d} />
              <p className="tres-petit discret">
                Souris ou doigt : glisser un équipement pour le déplacer, glisser le fond pour déplacer la vue, glisser la poignée d’un équipement choisi
                vers un autre pour les relier. Clavier : Tab pour choisir, flèches pour déplacer (Maj pour aller plus vite), Entrée pour modifier, Suppr pour supprimer.
              </p>
            </>
          ) : (
            <TableauSchema donnees={d} selection={selection} lectureSeule={!ecriture} majNoeud={majNoeud} majLiaison={majLiaison} supprimer={supprimer}
              ouvrir={(s) => ouvrirProprietes(s)} />
          )}
        </div>
        {!etroit && (
          <aside className="panneau cch-proprietes" ref={panneau} aria-label="Propriétés de la sélection">
            <div className="panneau-entete"><h2 style={{ fontSize: 'var(--t-l)' }}>{selection ? titreProps : 'Propriétés'}</h2></div>
            <div className="panneau-corps">
              {props ?? <p className="petit discret">Choisissez un équipement ou une liaison, dans le dessin ou dans le tableau, pour modifier ses propriétés, le relier à un autre équipement ou étayer une compatibilité.</p>}
            </div>
          </aside>
        )}
      </div>

      {etroit && (
        <Dialogue titre={selection ? titreProps : 'Propriétés'} ouvert={dialogue && !!selection} fermer={() => setDialogue(false)}
          pied={<Bouton variante="principal" onClick={() => setDialogue(false)}>Terminer</Bouton>}>
          {props}
        </Dialogue>
      )}

      <Dialogue titre="Conflit d’enregistrement" ouvert={!!ed.conflit && !conflitMasque} fermer={() => setConflitMasque(true)}
        pied={<>
          <Bouton onClick={ed.reprendreVersionEnregistree}>Reprendre la version enregistrée</Bouton>
          <Bouton variante="principal" onClick={ed.garderMaVersion}>Garder ma version</Bouton>
        </>}>
        {ed.conflit && (
          <div className="pile">
            <p>Ce schéma a été modifié{ed.conflit.misAJourPar ? ` par ${ed.conflit.misAJourPar}` : ''} pendant que vous travailliez (version {ed.conflit.revision}).</p>
            <ul className="petit">
              <li>Votre version : {d.nodes.length} équipement(s), {d.links.length} liaison(s).</li>
              <li>Version enregistrée : {ed.conflit.data.nodes.length} équipement(s), {ed.conflit.data.links.length} liaison(s).</li>
            </ul>
            <p className="petit secondaire">« Garder ma version » remplace la version enregistrée. « Reprendre la version enregistrée » abandonne vos modifications non enregistrées.</p>
          </div>
        )}
      </Dialogue>
      {confirmation}
    </div>
  );
}
